import { useQuery } from '@apollo/client';
import type { CustomAttributeFieldType } from '@risksmart-app/form-configuration/src/field-types/types';
import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormFieldConfig } from '@risksmart-app/shared/forms/types';
import { GetDepartmentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useCallback, useMemo } from 'react';
import type {
  ConditionalLogicOption,
  FieldTypeConditionMetadata,
} from 'src/blocks/form-editor-dialog';
import { fieldTypesConfig } from 'src/components/form/custom-attributes/field-types';
import type { FieldRendererProps } from 'src/components/form/custom-attributes/renderers/collection-layouts/types';
import { useCustomDatasourceHelpers } from 'src/pages/custom-datasources/useCustomDatasourceHelpers';
import { useFormConfigRegistry } from 'src/utils/table/hooks/form/useFormConfigRegistry';
import { getCustomAttributeRenderProps } from 'src/utils/table/utils/customAttributes';

import { useGetUsers } from '@/hooks/queries';
import { useGetTags } from '@/hooks/queries';

import { useCustomisableFormDataContext } from './customisable-form-data/CustomisableFormDataContext';
import { useFormFieldRegistryStore } from './stores/useFormFieldRegistryStore';

interface UseFormEditorConditionalOptionsResult {
  fieldOptions: ConditionalLogicOption[];
  getValueOptions: (fieldId: string) => ConditionalLogicOption[];
  /**
   * Resolves per-field-type metadata so the dialog can decide source
   * eligibility for in-session-new fields (e.g. a freshly-added text field
   * usable as a free-text source, or a userMultiselect with server-derived
   * value options) before save.
   */
  getFieldTypeMetadata: (
    type: string
  ) => FieldTypeConditionMetadata | undefined;
}

/**
 * Builds the conditional-logic source picker options for the form editor
 * dialog, mirroring the eligibility rules of the inline edit-field modal:
 *
 * - **allowAsSource**: a field can be the basis of a rule only when it has an
 *   enumerable value list (i.e. checkboxes can be rendered). For standard
 *   fields that means `allowAsConditionSource` plus a `displayType` whose
 *   values can be enumerated; for custom fields it means
 *   `fieldTypesConfig[type].allowAsConditionSource` and a non-empty
 *   `getConditionalPropertyFilterOptions` result.
 * - **allowAsTarget**: matches `EditFieldFields.tsx` — true for custom fields
 *   or standard fields with `allowTargetConditions`.
 *
 * `getValueOptions` returns the same enumerated values for any field — empty
 * for non-source fields. The dialog already prefers in-session edited options
 * over this fallback.
 */
export const useFormEditorConditionalOptions = (
  parentType: FormId,
  enabled: boolean
): UseFormEditorConditionalOptionsResult => {
  const formRegistry = useFormConfigRegistry();
  const formConfig = formRegistry[parentType];
  const helpers = useCustomDatasourceHelpers();
  const registeredFields = useFormFieldRegistryStore(
    (state) => state.getAllFields
  );
  const { customAttributeSchema } = useCustomisableFormDataContext();

  const { data: usersData } = useGetUsers({
    queryArgs: {},
    shouldSkip: !enabled,
  });
  const { data: departmentsData } = useQuery(GetDepartmentsDocument, {
    skip: !enabled,
  });
  const { data: tagsData } = useGetTags({
    queryArgs: {},
    shouldSkip: !enabled,
  });

  const users = useMemo(() => usersData?.user ?? [], [usersData]);
  const departmentTypes = useMemo(
    () => departmentsData?.department_type ?? [],
    [departmentsData]
  );
  const tagTypes = useMemo(() => tagsData?.tag_type ?? [], [tagsData]);

  const renderPropsByFieldId = useMemo(() => {
    if (!customAttributeSchema?.Schema || !customAttributeSchema?.UiSchema) {
      return new Map<string, FieldRendererProps>();
    }

    return new Map(
      getCustomAttributeRenderProps(
        customAttributeSchema.Schema,
        customAttributeSchema.UiSchema
      ).map((rp) => [rp.scope, rp])
    );
  }, [customAttributeSchema]);

  const standardFieldByFieldId = useMemo(() => {
    if (!formConfig) {
      return new Map<string, FormFieldConfig>();
    }

    return new Map(
      Object.values(formConfig).map((field) => [
        field.fieldId.toLowerCase(),
        field as FormFieldConfig,
      ])
    );
  }, [formConfig]);

  /**
   * Computes the enumerable value options for a field.
   *
   * - `null` → field is not source-eligible at all.
   * - `[]` (with `freeText: true`) → field is source-eligible but has no
   *   enumerable values (free-text only — e.g. plain text/title fields).
   * - `[...]` → field is source-eligible with a concrete value list.
   */
  const computeValueOptions = useCallback(
    (
      fieldId: string
    ): { options: ConditionalLogicOption[]; freeText: boolean } | null => {
      const customRenderProps = renderPropsByFieldId.get(fieldId);
      if (customRenderProps) {
        const fieldTypeConfig = fieldTypesConfig[customRenderProps.type];
        if (!fieldTypeConfig?.allowAsConditionSource) {
          return null;
        }
        const filterOptions =
          fieldTypeConfig.getConditionalPropertyFilterOptions?.(
            customRenderProps,
            { users, departmentTypes, tagTypes }
          );
        if (!filterOptions) {
          // Source-eligible but no enumerable values → free text.
          return { options: [], freeText: true };
        }

        return {
          options: filterOptions
            .filter(
              (o): o is { value: string; label: string } =>
                o.value != null && o.label != null
            )
            .map((o) => ({ value: o.value, label: o.label })),
          freeText: false,
        };
      }

      const standard = standardFieldByFieldId.get(fieldId.toLowerCase());
      if (!standard?.allowAsConditionSource) {
        return null;
      }
      const displayType = standard.displayType;
      if (!displayType) {
        // No displayType → free text.
        return { options: [], freeText: true };
      }

      switch (displayType.displayType) {
        case 'commonLookup':
          return {
            options: helpers
              .getCommonLookupOptions(displayType.i18nKey)
              .map((o: { value: string | number; label: string }) => ({
                value: String(o.value),
                label: o.label,
              })),
            freeText: false,
          };
        case 'rating': {
          if (
            helpers.hasScoringSettings &&
            displayType.ratingKey === 'likelihood'
          ) {
            return {
              options: helpers.likelihoodOptions.map(
                (o: { value: number; label: string }) => ({
                  value: String(o.value),
                  label: o.label,
                })
              ),
              freeText: false,
            };
          }
          if (
            helpers.hasScoringSettings &&
            displayType.ratingKey === 'impact'
          ) {
            return {
              options: helpers.impactOptions.map(
                (o: { value: number; label: string }) => ({
                  value: String(o.value),
                  label: o.label,
                })
              ),
              freeText: false,
            };
          }

          return {
            options: helpers
              .getRatingOptions(displayType.ratingKey)
              .filter(
                (o): o is typeof o & { value: string | number } =>
                  o.value != null
              )
              .map((o) => ({ value: String(o.value), label: o.label })),
            freeText: false,
          };
        }
        case 'users':
          return {
            options: users
              .filter(
                (u): u is typeof u & { Id: string } => typeof u.Id === 'string'
              )
              .map((u) => ({
                value: u.Id,
                label: u.FriendlyName ?? u.Email ?? u.Id,
              })),
            freeText: false,
          };
        case 'departments':
          return {
            options: departmentTypes.map((d) => ({
              value: d.DepartmentTypeId,
              label: d.Name,
            })),
            freeText: false,
          };
        case 'tags':
          return {
            options: tagTypes.map((t) => ({
              value: t.TagTypeId,
              label: t.Name,
            })),
            freeText: false,
          };
        default:
          // No enumeration for this displayType → free text.
          return { options: [], freeText: true };
      }
    },
    [
      renderPropsByFieldId,
      standardFieldByFieldId,
      users,
      departmentTypes,
      tagTypes,
      helpers,
    ]
  );

  const fieldOptions = useMemo<ConditionalLogicOption[]>(() => {
    const seen = new Set<string>();
    const result: ConditionalLogicOption[] = [];

    for (const reg of registeredFields()) {
      const lower = reg.fieldId.toLowerCase();
      if (seen.has(lower)) {
        continue;
      }
      seen.add(lower);

      const customRenderProps = renderPropsByFieldId.get(reg.fieldId);
      const standard = standardFieldByFieldId.get(lower);
      const isGroup = !reg.isCustom && !standard;

      const label =
        customRenderProps?.label ??
        standard?.formLabel ??
        reg.label ??
        reg.fieldId;

      const type =
        customRenderProps?.type ?? standard?.displayType?.displayType;

      const valueOptions = isGroup ? null : computeValueOptions(reg.fieldId);
      const allowAsSource = valueOptions !== null;

      const allowAsTarget = isGroup
        ? false
        : reg.isCustom
          ? true
          : Boolean(standard?.allowTargetConditions);

      result.push({
        value: reg.fieldId,
        label,
        type,
        allowAsSource,
        allowAsTarget,
        freeText: valueOptions?.freeText ?? false,
      });
    }

    return result;
  }, [
    registeredFields,
    renderPropsByFieldId,
    standardFieldByFieldId,
    computeValueOptions,
  ]);

  const getValueOptions = useCallback(
    (fieldId: string): ConditionalLogicOption[] =>
      computeValueOptions(fieldId)?.options ?? [],
    [computeValueOptions]
  );

  const getFieldTypeMetadata = useCallback(
    (type: string): FieldTypeConditionMetadata | undefined => {
      const config =
        fieldTypesConfig[type as CustomAttributeFieldType] ?? undefined;
      if (!config) {
        return undefined;
      }

      const allowAsConditionSource = config.allowAsConditionSource === true;

      // Probe the type's option provider with a minimal stub.
      // null result → free-text capture.
      let defaultValueOptions: ConditionalLogicOption[] | null = null;
      if (config.getConditionalPropertyFilterOptions) {
        const stubRenderProps: FieldRendererProps = {
          type: type as CustomAttributeFieldType,
          label: '',
          scope: '',
          path: '',
          options: [],
        };
        const opts = config.getConditionalPropertyFilterOptions(
          stubRenderProps,
          { users, departmentTypes, tagTypes }
        );
        defaultValueOptions = (opts ?? [])
          .filter(
            (o): o is { value: string; label: string } =>
              o.value != null && o.label != null
          )
          .map((o) => ({ value: o.value, label: o.label }));
      }

      return { allowAsConditionSource, defaultValueOptions };
    },
    [users, departmentTypes, tagTypes]
  );

  return { fieldOptions, getValueOptions, getFieldTypeMetadata };
};
